import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { managementAPI } from '../services/api';

const DataContext = createContext();

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState({});
  const [shiftsLoading, setShiftsLoading] = useState({});
  const cacheRef = useRef({});
  const shiftsCacheRef = useRef({});
  const inFlightRef = useRef({});
  const shiftsInFlightRef = useRef({});
  const cacheVersionRef = useRef({});
  const shiftsVersionRef = useRef(0);

  const getEmployeesPageBundle = useCallback(async (companyId) => {
    if (!companyId) return null;

    const cacheKey = `employees_bundle_${companyId}`;
    
    // Return cached data if available
    if (cacheRef.current[cacheKey] && cacheRef.current[cacheKey].timestamp > Date.now() - 30000) { // 30 seconds cache
      // console.log('[DataContext] Returning cached data for companyId:', companyId);
      return cacheRef.current[cacheKey].data;
    }

    // Prevent duplicate API calls
    if (inFlightRef.current[cacheKey]) {
      return inFlightRef.current[cacheKey];
    }

    const requestVersion = cacheVersionRef.current[cacheKey] || 0;
    const request = (async () => {
      console.log('[DataContext] Calling employeesPageBundleLight for companyId:', companyId);
      setLoading(prev => ({ ...prev, [cacheKey]: true }));
      
      const res = await managementAPI.employeesPageBundleLight(Number(companyId));
      const data = res.data || {};

      if ((cacheVersionRef.current[cacheKey] || 0) !== requestVersion) {
        return null;
      }
      cacheRef.current = {
        ...cacheRef.current,
        [cacheKey]: {
          data,
          timestamp: Date.now()
        }
      };
      return data;
    })();

    inFlightRef.current[cacheKey] = request;

    try {
      return await request;
    } finally {
      if (inFlightRef.current[cacheKey] === request) {
        delete inFlightRef.current[cacheKey];
        setLoading(prev => ({ ...prev, [cacheKey]: false }));
      }
    }
  }, []);

  const getEmployeeShiftsDetail = useCallback(async (employeeUserId, siteId) => {
    if (!employeeUserId || !siteId) return null;

    const cacheKey = `shifts_${employeeUserId}_${siteId}`;
    
    // Return cached data if available
    if (shiftsCacheRef.current[cacheKey] && shiftsCacheRef.current[cacheKey].timestamp > Date.now() - 30000) { // 30 seconds cache
      return shiftsCacheRef.current[cacheKey].data;
    }

    // Prevent duplicate API calls
    if (shiftsInFlightRef.current[cacheKey]) {
      return shiftsInFlightRef.current[cacheKey];
    }

    const requestVersion = shiftsVersionRef.current;
    const request = (async () => {
      console.log('[DataContext] Calling employeeShiftsDetail for employee:', employeeUserId, 'site:', siteId);
      setShiftsLoading(prev => ({ ...prev, [cacheKey]: true }));
      
      const res = await managementAPI.employeeShiftsDetail(employeeUserId, siteId);
      const data = res.data || {};

      if (shiftsVersionRef.current !== requestVersion) {
        return null;
      }
      shiftsCacheRef.current = {
        ...shiftsCacheRef.current,
        [cacheKey]: {
          data,
          timestamp: Date.now()
        }
      };
      return data;
    })();

    shiftsInFlightRef.current[cacheKey] = request;

    try {
      return await request;
    } finally {
      if (shiftsInFlightRef.current[cacheKey] === request) {
        delete shiftsInFlightRef.current[cacheKey];
        setShiftsLoading(prev => ({ ...prev, [cacheKey]: false }));
      }
    }
  }, []);

  const invalidateCache = useCallback((companyId) => {
    if (companyId) {
      const cacheKey = `employees_bundle_${companyId}`;
      cacheVersionRef.current = {
        ...cacheVersionRef.current,
        [cacheKey]: (cacheVersionRef.current[cacheKey] || 0) + 1
      };
      cacheRef.current = Object.fromEntries(
        Object.entries(cacheRef.current).filter(([key]) => key !== cacheKey)
      );
      delete inFlightRef.current[cacheKey];
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
      // Also clear related shifts cache
      shiftsVersionRef.current += 1;
      shiftsCacheRef.current = {};
      shiftsInFlightRef.current = {};
      setShiftsLoading({});
    } else {
      cacheVersionRef.current = {};
      shiftsVersionRef.current += 1;
      cacheRef.current = {};
      shiftsCacheRef.current = {};
      inFlightRef.current = {};
      shiftsInFlightRef.current = {};
      setLoading({});
      setShiftsLoading({});
    }
  }, []);

  const value = {
    getEmployeesPageBundle,
    getEmployeeShiftsDetail,
    invalidateCache,
    isLoading: (companyId) => loading[`employees_bundle_${companyId}`] || false,
    isShiftsLoading: (employeeUserId, siteId) => shiftsLoading[`shifts_${employeeUserId}_${siteId}`] || false,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
